/** biome-ignore-all lint/complexity/noUselessFragments: <explanation> */
import { Analytics } from '@cobliteam/analytics';
import type { Preferences } from '@cobliteam/common-types';
import { useLocalStorage } from '@cobliteam/hooks';
import type { AppState } from '@cobliteam/store';
import { changeMapStyle } from '@cobliteam/store/src/Map/actionCreators';
import { Button, Dialog, DropdownMenu, Tooltip, Typography } from '@cobliteam/ui/components';
import Label from '@cobliteam/ui/form/Label';
import RadioGroupItem, { RadioGroup } from '@cobliteam/ui/form/Radio';
import Icon from '@cobliteam/ui/icons';
import TagFeature from '@cobliteam/ui-business/components/Tag/Features';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AssetStyle, ColorStyle } from '../../MapStyles';

interface StyleOption {
  value: ColorStyle | AssetStyle;
  label: string;
  recommended?: boolean;
}

const colorOptions: StyleOption[] = [
  { value: 'grayscale', label: 'Cobli', recommended: true },
  { value: 'default', label: 'Padrão (Google Maps)' },

  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Escuro' },
];

const assetOptions: StyleOption[] = [
  { value: 'default', label: 'Completas (Padrão)' },
  { value: 'balanced', label: 'Simplificadas' },
  { value: 'minimal', label: 'Sem informações' },
];

const ColorInfoDialog = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button variant='link' color='default' size='sm' className='w-fit' icon={<Icon name='circle-help' size='sm' />} />
      </Dialog.Trigger>
      <Dialog.Content size='sm' portal>
        <Dialog.Header>
          <Dialog.Title>Cores do Mapa</Dialog.Title>
          <Dialog.Description className='sr-only' />
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Cobli (Recomendado)</Typography>
            <Typography variant='text-sm' color='secondary'>
              Reduz a saturação das cores do mapa base, mantendo contraste entre vias principais, secundárias e áreas urbanas. Esse estilo ajuda a destacar veículos, rotas, alertas
              e geofences, diminuindo distrações visuais. É o padrão mais indicado para monitoramento contínuo de frotas.
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Google Maps (Padrão)</Typography>
            <Typography variant='text-sm' color='secondary'>
              Usa o estilo oficial do Google Maps, com cores completas para ruas, áreas urbanas, parques, água e pontos de interesse. É o mapa mais detalhado visualmente, porém
              pode gerar mais ruído quando há muitos veículos ou eventos na tela.
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Claro</Typography>
            <Typography variant='text-sm' color='secondary'>
              Mantém o mapa em tons claros e neutros, com boa legibilidade de textos e ícones. Indicado para ambientes bem iluminados e para usuários que preferem maior contraste
              entre veículos, áreas monitoradas e o fundo do mapa.
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Escuro</Typography>
            <Typography variant='text-sm' color='secondary'>
              Aplica tons escuros ao mapa base, reduzindo o brilho geral da tela. Ajuda no uso prolongado e em ambientes com pouca luz, mantendo boa distinção de estradas, rodovias
              e veículos ativos.
            </Typography>
          </div>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

const AssetInfoDialog = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button variant='link' color='default' size='sm' className='w-fit' icon={<Icon name='circle-help' size='sm' />} />
      </Dialog.Trigger>
      <Dialog.Content size='sm' portal>
        <Dialog.Header>
          <Dialog.Title>Informações do Mapa</Dialog.Title>
          <Dialog.Description className='sr-only' />
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Completas (Padrão)</Typography>
            <Typography variant='text-sm' color='secondary'>
              Exibe a maior quantidade de informações disponíveis no Google Maps, incluindo nomes de ruas, bairros e pontos de interesse como hospitais, escolas, comércios e
              serviços. Indicado quando a localização detalhada do entorno é importante.
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Simplificadas</Typography>
            <Typography variant='text-sm' color='secondary'>
              Remove parte dos textos e pontos menos relevantes, mantendo ícones principais e referências importantes. Esse modo deixa o mapa mais limpo sem perder contexto,
              equilibrando informação e leitura visual.
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold'>Sem informações</Typography>
            <Typography variant='text-sm' color='secondary'>
              Mostra apenas os elementos essenciais do mapa, como rodovias, vias principais e limites geográficos. Remove a maioria dos pontos de interesse e rótulos, facilitando o
              foco total nos veículos, rotas e eventos operacionais.
            </Typography>
          </div>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

const MapStyleControl = () => {
  const dispatch = useDispatch();
  const mapStyle = useSelector((state: AppState) => state.map.mapStyle);
  const [localStorage, setLocalStorage] = useLocalStorage<Preferences>('preferences');

  const handleColorChange = (color: ColorStyle) => {
    Analytics.track('Map Style Color Changed', {
      color,
      context: 'tracking',
    });
    dispatch(changeMapStyle({ color }));
    setLocalStorage({
      ...(localStorage ?? {}),
      mapStyle: {
        ...(localStorage?.mapStyle ?? {}),
        color,
      } as Preferences['mapStyle'],
    });
  };

  const handleAssetChange = (asset: AssetStyle) => {
    Analytics.track('Map Style Asset Changed', {
      asset,
      context: 'tracking',
    });
    dispatch(changeMapStyle({ asset }));
    setLocalStorage({
      ...(localStorage ?? {}),
      mapStyle: {
        ...(localStorage?.mapStyle ?? {}),
        asset,
      } as Preferences['mapStyle'],
    });
  };

  // Load map style from localStorage on mount
  useEffect(() => {
    if (localStorage?.mapStyle) {
      const { color, asset } = localStorage.mapStyle;
      if (color && color !== mapStyle.color) {
        dispatch(changeMapStyle({ color }));
      }
      if (asset && asset !== mapStyle.asset) {
        dispatch(changeMapStyle({ asset }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <div className='relative'>
            <Tooltip content='Ajustes do mapa' asChild>
              <div>
                <TagFeature.New label='Novo' color='success' variant='contained' className='-top-2 -right-7 absolute' />
                <Button
                  variant='outlined'
                  color='default'
                  icon={<Icon name='layers' className='text-gray-11' />}
                  data-testid='map-style-control-trigger'
                  className='bg-white shadow-md'
                />
              </div>
            </Tooltip>
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content portal={false} align='start' className='w-64 p-0' data-testid='map-style-control-content'>
          <div className='flex flex-col space-y-4 p-4'>
            {/* Header */}
            <div className='border-gray-6 border-b pb-2'>
              <Typography variant='text-md-semibold'>Estilo do Mapa</Typography>
            </div>

            {/* Cores Section */}
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <Typography variant='text-md-medium' color='secondary'>
                  Cor do mapa
                </Typography>
                <ColorInfoDialog />
              </div>
              <RadioGroup value={mapStyle.color} onValueChange={handleColorChange} className='space-y-2'>
                {colorOptions.map((option) => (
                  <div key={option.value} className='flex items-center gap-2'>
                    <RadioGroupItem value={option.value} id={`color-${option.value}`} />
                    <Label htmlFor={`color-${option.value}`} className='cursor-pointer'>
                      <Typography variant='text-sm'>
                        {option.label} {option.recommended && <span className='font-medium text-green-6'>- (Recomendando)</span>}
                      </Typography>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Divider */}
            <div className='border-gray-6 border-t' />

            {/* Informações Section */}
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <Typography variant='text-md-medium' color='secondary'>
                  Informações no mapa
                </Typography>
                <AssetInfoDialog />
              </div>
              <RadioGroup value={mapStyle.asset} onValueChange={handleAssetChange} className='space-y-2'>
                {assetOptions.map((option) => (
                  <div key={option.value} className='flex items-center gap-2'>
                    <RadioGroupItem value={option.value} id={`asset-${option.value}`} />
                    <Label htmlFor={`asset-${option.value}`} className='cursor-pointer'>
                      <Typography variant='text-sm'>{option.label}</Typography>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu>
    </>
  );
};

export default memo(MapStyleControl);
